import React, { useCallback, useRef, useState } from 'react';
import {
  SaveButton,
  CREATE,
  UPDATE,
  fetchEnd,
  fetchStart,
  refreshView
} from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { connect } from 'react-redux';

import UserPhoneForm from './UserPhoneForm';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';

const UserPhoneCreateUpdate = ({
  operation,
  fetchStart,
  fetchEnd,
  handleDialogToggle,
  id,
  userPhoneId,
  refreshView,
  popupTitle,
  okButtonLabel,
  ...operationProps
}) => {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const notifyServerError = useNotifyServerError();

  const handleSaveClick = useCallback(() => {
    // Trigger a submit of our custom quick create/update form
    // This is needed because our modal action buttons are oustide the form
    formRef.current.submit();
  }, [formRef]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    formRef.current.reset();
    handleDialogToggle(state => !state);
  }, [submitting, formRef, handleDialogToggle]);

  const handleSubmit = useCallback(
    values => {
      // Dispatch an action letting react-admin know a API call is ongoing
      fetchStart();
      setSubmitting(true);

      // Get user id, if it's during creation  it come from the record
      // or if editing it come from userphone id : "userId:phoneId"
      const userId = operation === 'Create' ? id : userPhoneId.split(':')[0];

      // As we want to know when the new phone has been created/updated in order to close the modal, we use the
      // dataProvider directly
      const crudOperation = operation === 'Create' ? CREATE : UPDATE;
      try {
        dataProvider(crudOperation, 'userphones', {
          data: { userId, ...values }
        })
          .then(() => {
            setSubmitting(false);
            handleDialogToggle(state => !state);
            refreshView();
            setTimeout(() => formRef.current && formRef.current.reset());
            // Dispatch an action letting react-admin know a API call has ended
            fetchEnd();
          })
          .catch(error => {
            notifyServerError(error);
            fetchEnd();
          });
      } catch (error) {
        notifyServerError(error);
        fetchEnd();
      }
    },
    [
      fetchEnd,
      fetchStart,
      handleDialogToggle,
      id,
      operation,
      formRef,
      refreshView,
      notifyServerError,
      userPhoneId
    ]
  );

  return (
    <Dialog
      fullWidth
      open={true}
      aria-label={`${operation} user phone`}
      maxWidth="sm"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        {popupTitle}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <UserPhoneForm
          formRef={formRef}
          {...operationProps}
          operation={operation}
          handleSubmit={handleSubmit}
          id={userPhoneId}
        />
      </DialogContent>
      <DialogActions>
        <SaveButton
          style={{ minWidth: '82px' }}
          saving={submitting}
          handleSubmitWithRedirect={handleSaveClick}
          label={okButtonLabel}
          icon={<></>}
          id="add-button"
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          id="cancel-button"
          disabled={submitting}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  userphones: state.admin.resources.userphones, // List of user phones
  userphonetypes: state.admin.resources.userphonetypes // List of user phone types
});

const mapDispatchToProps = {
  fetchEnd,
  fetchStart,
  refreshView
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserPhoneCreateUpdate);
